import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "../lib/api";
import ImageUploader from "./ImageUploader";
import { useAuth } from "../contexts/AuthContext";

// Round 115/119 — Home page is LOCKED for partner/business roles until they
// upload their organization logo AND add a Business Name (shown publicly for
// privacy instead of the owner's personal name). Customers, students, area
// coordinators, managers and admins are exempt; company-linked staff inherit
// the org's logo & name.
function useBrandStatus() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get("/auth/logo/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  return [status, setStatus];
}

function BusinessNameForm({ status, onSaved }) {
  const [name, setName] = useState(status.business_source === "own" ? status.business_name : "");
  const save = async () => {
    try {
      const r = await api.post("/auth/business-name", { name });
      onSaved(r.data);
      toast.success("Business name saved ✅");
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="flex gap-2">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Business name *"
        className="w-full px-3 py-2.5 bg-white text-black border border-[#26263A] rounded-xl text-sm"
        data-testid="business-name-input" />
      <button onClick={save} className="px-4 rounded-xl bg-[#10B981] text-black text-[11px] font-black"
        data-testid="business-name-save">Save</button>
    </div>
  );
}

export default function LogoGate({ active = true }) {
  const [status, setStatus] = useBrandStatus();
  const { user, setUser } = useAuth();
  const onBusinessNameSaved = (s) => {
    setStatus(s);
    if (s.business_name && user) setUser({ ...user, business_name: s.business_name, display_name: s.business_name });
    if (s.complete) toast.success("Home Page unlocked ✅", { duration: 7000 });
  };
  if (!active || !status || !status.required || status.complete) return null;
  const onLogo = async (url) => {
    if (!url) return;
    try {
      const r = await api.post("/auth/logo", { url });
      setStatus(r.data);
      if (r.data.complete) toast.success("Business identity complete. Home Page unlocked ✅", { duration: 7000 });
      else toast.success("Logo saved ✅");
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="fixed inset-0 z-[85] bg-black/90 backdrop-blur-md flex items-center justify-center p-4" data-testid="logo-gate">
      <div className="bg-[#13131E] border border-[#F5B400]/50 rounded-2xl p-5 w-full max-w-sm text-center space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="text-4xl">🏷️</div>
        <div className="text-base font-black text-[#F5B400]" data-testid="logo-gate-warning">Your Home Page is temporarily locked</div>
        <p className="text-xs text-[#C7D2FE]">Complete your <b>business identity</b> to continue. it appears on your receipts, tickets, documents and the public partner directory (your personal name stays private).</p>
        {!status.has_business_name && (
          <div className="text-left space-y-1">
            <div className="text-[9px] uppercase tracking-widest font-black text-[#9CA3D4]">1 · Business name</div>
            <BusinessNameForm status={status} onSaved={onBusinessNameSaved} />
          </div>
        )}
        {!status.has_logo && (
          <div className="text-left space-y-1">
            <div className="text-[9px] uppercase tracking-widest font-black text-[#9CA3D4]">{status.has_business_name ? "" : "2 · "}Business logo</div>
            <ImageUploader kind="org_logo" label="Organization logo *" onUploaded={onLogo} testId="logo-gate-upload" />
          </div>
        )}
      </div>
    </div>
  );
}

// Profile-page section: view / replace the logo & business name.
export function LogoSection() {
  const [status, setStatus] = useBrandStatus();
  const { user, setUser } = useAuth();
  const onBusinessNameSaved = (s) => {
    setStatus(s);
    if (s.business_name && user) setUser({ ...user, business_name: s.business_name, display_name: s.business_name });
  };
  if (!status || !status.required) return null;
  const onLogo = async (url) => {
    if (!url) return;
    try {
      const r = await api.post("/auth/logo", { url });
      setStatus(r.data);
      toast.success("Logo updated ✅");
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="surface rounded-2xl p-5 space-y-3" data-testid="profile-logo-section">
      <div className="text-[10px] uppercase tracking-widest font-black text-[#147D60]">Business identity · name & logo</div>
      <p className="text-[10px] text-[#60727A]">
        Your business name and logo appear on receipts, tickets, PDF documents and the public partner directory. Your personal name stays private.
        {status.source === "org" && status.org_name ? ` Currently inherited from ${status.org_name}.` : ""}
      </p>
      <BusinessNameForm status={status} onSaved={onBusinessNameSaved} />
      {status.business_name && <div className="text-[10px] text-[#60727A]">Displayed publicly as: <b className="text-[#1A303B]">{status.business_name}</b></div>}
      <ImageUploader kind="org_logo" value={status.source === "own" ? status.logo_url : ""}
                     onUploaded={onLogo} label="Logo file" testId="profile-logo-upload" />
    </div>
  );
}
